import React, { useEffect, useState } from 'react';
import type { GuardSiteDetailDto } from '../../../../shared/types';
import type { Ctx } from '../ctx';
import { gateKeys, stageNames, taskStage, taskTool, type Plan, type Task, type Workspace } from './SecurityPlan';

interface Asset { id: number; name: string; kind: string; address: string | null; status: 'candidate' | 'confirmed' | 'excluded'; notes: string | null }
type Filter = 'all' | 'noguard' | 'nocs' | 'confirm';

const crowdStrike = (notes: string | null): { label: string; tone: 'ok' | 'bad' | 'none' } => {
  const m = /CrowdStrike (attivo|rilevato|non rilevato|non verificato)/.exec(notes ?? '');
  if (!m) return { label: 'Sconosciuto', tone: 'none' };
  if (m[1] === 'attivo') return { label: 'CrowdStrike attivo', tone: 'ok' };
  if (m[1] === 'rilevato') return { label: 'CrowdStrike fermo', tone: 'bad' };
  if (m[1] === 'non rilevato') return { label: 'Senza CrowdStrike', tone: 'bad' };
  return { label: 'Non verificato', tone: 'none' };
};
const assetStatus = { candidate: 'Da confermare', confirmed: 'Confermato', excluded: 'Escluso' };
const ago = (iso: string) => {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  return days <= 0 ? 'oggi' : days === 1 ? 'ieri' : `${days} giorni fa`;
};

const groups: { id: string; label: string; match: (t: Task) => boolean }[] = [
  { id: 'urg', label: 'Lavori urgenti', match: (t) => t.kind === 'azione' && t.priority === 'Urgente' },
  { id: 'imp', label: 'Lavori importanti', match: (t) => t.kind === 'azione' && t.priority === 'Importante' },
  { id: 'prog', label: 'Da programmare', match: (t) => t.kind === 'azione' && t.priority !== 'Urgente' && t.priority !== 'Importante' },
  { id: 'doc', label: 'Documenti e autorizzazioni', match: (t) => t.kind === 'documento' },
  { id: 'ver', label: 'Verifiche', match: (t) => t.kind === 'verifica' },
];

export function SecurityOverview({ ctx, site, onNavigate }: { ctx: Ctx; site: GuardSiteDetailDto; onNavigate: (view: Workspace) => void }) {
  const [plan, setPlan] = useState<Plan | null>(null);
  const [assets, setAssets] = useState<Asset[] | null>(null);
  const [filter, setFilter] = useState<Filter>('all');

  useEffect(() => {
    let alive = true;
    void ctx.api.get<Plan>(`/api/guard/sites/${site.id}/plan`).then((p) => { if (alive) setPlan(p); }).catch(() => { if (alive) setPlan({ clientName: null, importedAt: null, tasks: [] }); });
    void ctx.api.get<Asset[]>(`/api/guard/sites/${site.id}/assets`).then((a) => { if (alive) setAssets(a); }).catch(() => { if (alive) setAssets([]); });
    return () => { alive = false; };
  }, [ctx.api, site.id]);

  const tasks = plan?.tasks ?? [];
  const done = tasks.filter((t) => t.status === 'fatto').length;
  const pending = tasks.filter((t) => t.status !== 'fatto');
  const ordered = [...pending].sort((a, b) => taskStage(a) - taskStage(b) || gateKeys.indexOf(a.key) - gateKeys.indexOf(b.key) || a.id - b.id);
  const next = ordered.find((t) => t.status !== 'in_attesa') ?? ordered[0];
  const nextTool = next ? taskTool(next) : null;
  const urgent = pending.filter((t) => t.kind === 'azione' && t.priority === 'Urgente').slice(0, 3);

  const pcs = (assets ?? []).filter((a) => a.kind === 'device' && a.status !== 'excluded');
  const guardNames = new Set(site.deviceList.filter((d) => d.lastSeen !== null).map((d) => d.name.toUpperCase()));
  const withGuard = (a: Asset) => guardNames.has(a.name.toUpperCase()) || guardNames.has((a.address ?? '').toUpperCase());
  const rows = pcs.filter((a) => filter === 'all' || (filter === 'noguard' && !withGuard(a)) || (filter === 'nocs' && crowdStrike(a.notes).tone !== 'ok') || (filter === 'confirm' && a.status === 'candidate'));
  const noGuard = pcs.filter((a) => !withGuard(a)).length;
  const noCs = pcs.filter((a) => crowdStrike(a.notes).tone !== 'ok').length;
  const toConfirm = pcs.filter((a) => a.status === 'candidate').length;
  const openAlerts = site.events.filter((e) => !e.ackAt && ['medium', 'high', 'critical'].includes(e.severity)).length;

  const recent = [...tasks].filter((t) => t.status !== 'da_fare').sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)).slice(0, 4);
  const pct = tasks.length ? Math.round((done / tasks.length) * 100) : 0;

  return <div className="sov">
    {next ? <div className="sov-next">
      <div>
        <div className="sov-eyebrow">PROSSIMO LAVORO · {stageNames[taskStage(next)].toUpperCase()}</div>
        <h3>{next.title}</h3>
        <p>{next.status === 'in_attesa' ? 'In attesa di risposta o di un documento. ' : ''}{next.reason || next.instructions}</p>
      </div>
      <button type="button" className="sov-btn primary" onClick={() => onNavigate(nextTool?.view ?? 'guide')}>{nextTool ? nextTool.label : 'Apri il lavoro'} →</button>
    </div> : tasks.length === 0 ? <div className="sov-next">
      <div><div className="sov-eyebrow">PRIMO PASSO</div><h3>Importa il piano del cliente</h3><p>Crea il piano con Tecnoside Piano e caricalo nella guida: da lì parte tutto il lavoro.</p></div>
      <button type="button" className="sov-btn primary" onClick={() => onNavigate('guide')}>Apri la guida →</button>
    </div> : null}

    <div className="sov-kpis">
      <div className="sov-k"><span>PC censiti</span><b>{pcs.length}</b><small>da Assessment e inventario</small></div>
      <div className="sov-k"><span>PC che hanno inviato dati</span><b className={guardNames.size === 0 ? 'bad' : ''}>{site.deviceList.filter((d) => d.lastSeen !== null).length}</b><small>{site.online} in linea ora · {site.devices} registrati</small></div>
      <div className="sov-k"><span>Avvisi da vedere</span><b className={openAlerts > 0 ? 'bad' : ''}>{openAlerts}</b><small>{openAlerts > 0 ? 'da guardare' : 'nessun avviso'}</small></div>
      <div className="sov-k"><span>Lavori completati</span><b>{done}<em> / {tasks.length}</em></b><div className="sov-bar"><i style={{ width: `${pct}%` }} /></div></div>
    </div>

    <div className="sov-cols">
      <div>
        <section className="sov-panel">
          <header><b>Dispositivi</b><span>{pcs.length} registrati</span></header>
          <div className="sov-chips" role="tablist" aria-label="Filtra i dispositivi">
            {([['all', `Tutti ${pcs.length}`], ['noguard', `Senza Guard ${noGuard}`], ['nocs', `Senza CrowdStrike attivo ${noCs}`], ['confirm', `Da confermare ${toConfirm}`]] as [Filter, string][]).map(([id, label]) =>
              <button key={id} type="button" role="tab" aria-selected={filter === id} className={`sov-chip${filter === id ? ' on' : ''}`} onClick={() => setFilter(id)}>{label}</button>)}
          </div>
          {assets === null ? <p className="sov-empty">Caricamento…</p> : rows.length === 0 ? <p className="sov-empty">Nessun dispositivo in questo elenco.</p> :
            <table className="sov-table">
              <thead><tr><th>Nome</th><th>Protezione</th><th>Inventario</th><th>Guard</th></tr></thead>
              <tbody>{rows.map((a) => {
                const cs = crowdStrike(a.notes);
                const g = withGuard(a);
                return <tr key={a.id}>
                  <td><b>{a.name}</b></td>
                  <td><span className={`sov-st ${cs.tone}`}>{cs.label}</span></td>
                  <td><span className={`sov-st ${a.status === 'confirmed' ? 'ok' : 'none'}`}>{assetStatus[a.status]}</span></td>
                  <td><span className={`sov-st ${g ? 'ok' : 'none'}`}>{g ? 'Collegato' : 'Non collegato'}</span></td>
                </tr>;
              })}</tbody>
            </table>}
          <footer><button type="button" className="sov-btn" onClick={() => onNavigate('assets')}>Apri l’inventario</button>{site.devices > 0 && <button type="button" className="sov-btn" onClick={() => onNavigate('devices')}>Vedi PC e avvisi</button>}</footer>
        </section>

        <section className="sov-panel">
          <header><b>Attività recenti</b><span>dal piano</span></header>
          {recent.length === 0 ? <p className="sov-empty">Ancora nessuna attività.</p> : <ul className="sov-tl">
            {recent.map((t) => <li key={t.id}><i className={t.status === 'fatto' ? 'ok' : 'warn'} /><div>{t.title}<small>{t.status === 'fatto' ? 'Fatto' : t.status === 'in_attesa' ? 'In attesa' : 'In corso'} · {ago(t.updatedAt)}</small></div></li>)}
          </ul>}
        </section>
      </div>

      <div>
        <section className="sov-panel">
          <header><b>Da fare adesso</b><span>urgenti</span></header>
          {urgent.length === 0 ? <p className="sov-empty">Nessun lavoro urgente aperto.</p> : urgent.map((t) => <div key={t.id} className="sov-todo"><b>{t.title}</b><span><em className="sov-tag">URGENTE</em>{t.owner}</span></div>)}
          <footer><button type="button" className="sov-btn" onClick={() => onNavigate('guide')}>Vai alla guida</button></footer>
        </section>

        <section className="sov-panel">
          <header><b>Avanzamento</b><span>{tasks.length} voci</span></header>
          {groups.map((g) => {
            const list = tasks.filter(g.match);
            if (list.length === 0) return null;
            const d = list.filter((t) => t.status === 'fatto').length;
            return <div key={g.id} className="sov-prog"><div><span>{g.label}</span><span>{d} / {list.length}</span></div><div className="sov-bar"><i style={{ width: `${Math.round((d / list.length) * 100)}%` }} /></div></div>;
          })}
        </section>
      </div>
    </div>
  </div>;
}
